/* eslint-disable no-nested-ternary */
/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, IconButton, Link, Typography, useTheme } from '@mui/material';

import IconEdit from '@assets/icons/edit.svg?react';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { RootState } from '../../../redux/store';
import { StructureType } from '../../../types/types';
import {
 findStructure,
 getTranslation,
 isJson,
} from '../../../utils/dataTypes';
import { titleStyles } from './infoStyles';
import { CustomGridApi } from '../XTable/types';

type GroupedFields = StructureType & {
 Key: string;
 Translation: string;
 Group: string;
 Value: any;
 InnerValue: string;
};

const InfoList = ({
 data,
 apiRef,
 customFields,
}: {
 data: any;
 customFields?: string[] | null;
 apiRef?: MutableRefObject<CustomGridApi>;
}) => {
 const { pageName } = usePage();
 const th = useTheme();
 //  const { openDialog: openFieldsConfigDialog } = useFieldsConfig();
 const { t } = useTranslation();

 const structure = useSelector((state: RootState) => {
  if (pageName) {
   const pageState = state[pageName as keyof RootState];
   if (pageState && typeof pageState === 'object' && 'structure' in pageState) {
    return pageState.structure;
   }
  }
  return null;
 });

 const [fields, setFields] = useState<string[]>([]);

 const handleEditClick = () => {
  if (apiRef && apiRef.current) {
   apiRef.current?.setModalState({
    id: data.Id,
    action: 'update',
    //  tab: 1,
   });
  }
 };

 const fieldsWithData = useMemo(() => {
  if (!structure || !Array.isArray(fields)) return [];
  return fields?.map((field) => {
   const val = field.split('.').reduce((acc, part) => acc?.[part], data);
   const str = findStructure(structure, field);

   const idVar = isJson(str?.ForeignKeyDefaultFilter || '')
    ? JSON.parse(str?.ForeignKeyDefaultFilter || '').fields.split(',')[0]
    : 'Id';

   const nameVar = isJson(str?.ForeignKeyDefaultFilter || '')
    ? JSON.parse(str?.ForeignKeyDefaultFilter || '').fields.split(',')[1]
    : 'Name';

   const innerValue = data?.[field]?.[idVar];

   //  console.log(field, val, nameVar);
   let value = val;
   if (str?.Type === 'model' && val) {
    value = val[nameVar];
   }
   if (str?.Type === 'array') {
    value = t('user.sidebar.infoList.noArraySupport');
   }
   if (str?.Type === 'boolean') {
    value =
     typeof val === 'boolean' ? (val ? t('global.yes') : t('global.no')) : '-';
   }
   if (!val) {
    value = '-';
   }

   return {
    ...str,
    Key: field,
    Translation: getTranslation(structure, field),
    Value: value,
    InnerValue: innerValue,
    Group: str?.Group,
   };
  });
 }, [fields, data, structure]);

 const groupsMemo = useMemo(() => {
  const gr = fieldsWithData
   .filter((f) => f.Type !== 'array')
   .reduce(
    (acc, f) => {
     acc[f.Group] = acc[f.Group] || [];
     acc[f.Group].push(f);
     return acc;
    },
    {} as Record<string, GroupedFields[]>,
   );

  const sortedGroups = Object.keys(gr).sort((a, b) => {
   // Put null (or string "null") at the end
   if (a === 'null') return 1;
   if (b === 'null') return -1;

   // Standard string comparison
   return a.localeCompare(b);
  });
  const sortedObj: Record<string, GroupedFields[]> = {};
  for (const key of sortedGroups) {
   sortedObj[key] = gr[key];
  }

  return sortedObj;
 }, [fieldsWithData]);

 useEffect(() => {
  setFields(customFields || Object.keys(data || {}));
 }, [data, customFields]);

 return Object.keys(groupsMemo).map((group: string) => (
  <Box key={group}>
   <Typography
    sx={{
     ...titleStyles,
     mb: 2,
     display: 'flex',
     alignItems: 'center',
     gap: 1,
     justifyContent: 'space-between',
    }}
   >
    {group === 'null' ? t('global.data') : group}{' '}
    <IconButton onClick={() => handleEditClick()} disabled={!apiRef}>
     <IconEdit style={{ color: th.palette.kemek.darkestGray }} />
    </IconButton>
   </Typography>

   <Box
    sx={{
     display: 'flex',
     flexDirection: 'column',
     gap: '12px',
     border: `1px solid ${th.palette.kemek.lightestGray}`,
     borderRadius: 1,
     p: 2,
     mb: 3,
    }}
   >
    {!fieldsWithData.length && (
     <Box
      sx={{
       textAlign: 'center',
       p: '12px',
       backgroundColor: th.palette.kemek.lightBlue,
      }}
     >
      <Typography variant="body2" color="text.secondary">
       {t('global.noItemsFound')}
      </Typography>
     </Box>
    )}
    {group &&
     groupsMemo[group]?.map((item: GroupedFields) => {
      const hasSubArray =
       item.ForeignKeyUrl && item.ForeignKeyUrl.split('/').length > 4;
      const href =
       item.ForeignKeyUrl && item.Value !== '-'
        ? `${item.ForeignKeyUrl.split('/')[2]?.toLowerCase()}?id=${
           item.InnerValue
          }`
        : '';
      return (
       <Box key={item.Key}>
        <Box
         sx={{
          display: 'flex',
          gap: 1, //   alignItems: 'center',
          justifyContent: 'space-between',
         }}
        >
         <Typography
          component="span"
          sx={{
           fontWeight: 'medium',
           fontSize: 12,
           //  color: !item.Value ? customColors.gray : 'inherit',
          }}
         >
          {item.Translation}
         </Typography>
         {item.Type === 'model' && item.Value !== '-' && !hasSubArray ? (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link
           href={href}
           sx={{ fontSize: 12, textAlign: 'right', wordBreak: 'break-word' }}
          >
           {item.Value}
          </Link>
         ) : (
          <Typography
           sx={{ fontSize: 12, textAlign: 'right', wordBreak: 'break-word' }}
          >
           {item.Value}
          </Typography>
         )}
        </Box>
       </Box>
      );
     })}
   </Box>
  </Box>
 ));
};

export default InfoList;
